import React from 'react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error('ErrorBoundary caught:', error, info);

    // ChunkLoadError = stale browser cache after a new deployment.
    // Hard-reload once to fetch fresh chunks. Guard against reload loops
    // by checking a sessionStorage flag.
    const isChunkError = error && (
      error.name === 'ChunkLoadError' ||
      /loading chunk/i.test(error.message) ||
      /loading css chunk/i.test(error.message)
    );
    if (isChunkError) {
      try {
        if (!sessionStorage.getItem('__chunk_reload')) {
          sessionStorage.setItem('__chunk_reload', '1');
          window.location.reload();
          return;
        }
      } catch {}
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    const error = this.state.error;
    const isChunkError = error && (
      error.name === 'ChunkLoadError' ||
      /loading chunk/i.test(error.message || '')
    );

    if (isChunkError) {
      return (
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          minHeight: '60vh', padding: '40px 24px', textAlign: 'center',
          fontFamily: "'Inter', sans-serif",
        }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🔄</div>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 10 }}>
            Update available
          </h2>
          <p style={{ fontSize: 14, color: 'var(--text-tertiary)', marginBottom: 28, maxWidth: 360, lineHeight: 1.6 }}>
            A new version of the app was deployed. Refreshing to load it…
          </p>
          <button
            onClick={() => { try { sessionStorage.removeItem('__chunk_reload'); } catch {} window.location.reload(); }}
            style={{ padding: '10px 22px', background: 'var(--primary)', color: 'white', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
          >
            Refresh now
          </button>
        </div>
      );
    }

    return (
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        minHeight: '60vh', padding: '40px 24px', textAlign: 'center',
        fontFamily: "'Inter', sans-serif",
      }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 10, letterSpacing: '-0.3px' }}>
          Something went wrong
        </h2>
        <p style={{ fontSize: 14, color: 'var(--text-tertiary)', marginBottom: 28, maxWidth: 360, lineHeight: 1.6 }}>
          This page ran into an unexpected error. Try refreshing.
        </p>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
          <button
            onClick={() => window.location.reload()}
            style={{ padding: '10px 22px', background: 'var(--primary)', color: 'white', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
          >
            Reload page
          </button>
          <button
            onClick={() => { this.setState({ hasError: false, error: null }); window.history.back(); }}
            style={{ padding: '10px 22px', background: 'var(--bg-card)', color: 'var(--text-primary)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
          >
            Go back
          </button>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
